// monitor/src/App.tsx
import React, { useEffect, useState } from "react";
import { API_BASE } from "./apiBase";
import { OrganizationDebug, ProjectDebug, SqlResult, UserDebug } from "./MonitorTypes";

const POLL_MS = 1000;

function formatDateTime(value: string | null | undefined): string {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const pad = (n: number) => n.toString().padStart(2, "0");
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  const ss = pad(d.getSeconds());
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
}

function formatPercent(
  value: number | null | undefined,
  digits: number = 1,
): string {
  if (value === null || value === undefined) return "-";

  const pct = value * 100;

  return `${pct.toFixed(digits).replace(".", ",")}%`;
}

function formatSeconds(value: number | null | undefined): string {
  if (value === null || value === undefined) return "-";
  const total = Math.floor(value);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${h}h ${m}m ${s}s`;
}

function kpiLabel(label: string, tooltip: string) {
  return (
    <span title={tooltip} style={{ cursor: "help", textDecoration: "underline dotted" }}>
      {label}
    </span>
  );
}

function exportCsv(
  filename: string,
  header: string[],
  rows: (string | number | null | undefined)[][]
) {
  const escape = (v: string | number | null | undefined): string => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    if (s.includes('"') || s.includes(";") || s.includes("\n")) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };

  const lines = [
    header.map(escape).join(";"),
    ...rows.map((row) => row.map(escape).join(";")),
  ];
  const csv = lines.join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function exportText(filename: string, content: string) {
  const blob = new Blob([content ?? ""], {
    type: "text/plain;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function MonitorApp() {
  const [users, setUsers] = useState<UserDebug[]>([]);
  const [projects, setProjects] = useState<ProjectDebug[]>([]);
  const [organizations, setOrganizations] = useState<OrganizationDebug[]>([]);

  const [error, setError] = useState<string | null>(null);
  const [dbDown, setDbDown] = useState(false);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);

  // Collapse-States für Tabellen
  const [showProjects, setShowProjects] = useState<boolean>(true);
  const [showUsers, setShowUsers] = useState<boolean>(true);
  const [showOrgs, setShowOrgs] = useState<boolean>(true);

  // SQL-Konsole
  const [sqlInput, setSqlInput] = useState<string>("");
  const [sqlResult, setSqlResult] = useState<SqlResult | null>(null);
  const [sqlBusy, setSqlBusy] = useState<boolean>(false);

  useEffect(() => {
    let timer: number | undefined;

    const fetchAll = async () => {
      try {
        const [uRes, pRes, oRes] = await Promise.all([
          fetch(`${API_BASE}/api/debug/users`),
          fetch(`${API_BASE}/api/debug/projects`),
          fetch(`${API_BASE}/api/debug/organizations`),
        ]);

        if (uRes.status === 503 || pRes.status === 503 || oRes.status === 503) {
          setDbDown(true);
          setError("DB derzeit nicht erreichbar. Versuchen Sie es später erneut.");
          return;
        }

        if (!uRes.ok || !pRes.ok || !oRes.ok) {
          const uText = await uRes.text();
          const pText = await pRes.text();
          const oText = await oRes.text();
          throw new Error(
            `Users: ${uRes.status} ${uText} | Projects: ${pRes.status} ${pText} | Orgs: ${oRes.status} ${oText}`
          );
        }

        const uJson = (await uRes.json()) as UserDebug[];
        const pJson = (await pRes.json()) as ProjectDebug[];
        const oJson = (await oRes.json()) as OrganizationDebug[];

        // Sortierung Users / Projects nach letztem Login / Solve-Zeit
        uJson.sort((a, b) => {
          const da = a.login_at ? new Date(a.login_at).getTime() : 0;
          const db = b.login_at ? new Date(b.login_at).getTime() : 0;
          return db - da;
        });

        pJson.sort((a, b) => {
          const urgencyA = a.solve_urgency ?? 0;
          const urgencyB = b.solve_urgency ?? 0;
          if (urgencyA !== urgencyB) return urgencyB - urgencyA;

          const solveA = a.solve_at ? new Date(a.solve_at).getTime() : 0;
          const solveB = b.solve_at ? new Date(b.solve_at).getTime() : 0;
          if (solveA !== solveB) return solveB - solveA;

          return b.id - a.id;
        });

        setUsers(uJson);
        setProjects(pJson);
        setOrganizations(oJson);
        setError(null);
        setDbDown(false);
      } catch (e: any) {
        setError(e?.message ?? "Fehler beim Laden der Debug-Daten");
      }
    };

    // einmal sofort
    fetchAll();

    // nur Intervall, wenn nicht pausiert
    if (!isPaused) {
      // @ts-ignore
      timer = window.setInterval(fetchAll, POLL_MS);
    }

    return () => {
      if (timer !== undefined) {
        window.clearInterval(timer);
      }
    };
  }, [isPaused]);

  // wenn selektiertes Projekt gelöscht wurde → zurück in Auto-Modus
  useEffect(() => {
    if (selectedProjectId === null) return;
    const exists = projects.some((p) => p.id === selectedProjectId);
    if (!exists) {
      setSelectedProjectId(null);
    }
  }, [projects, selectedProjectId]);

  // ---------------- KPIs ----------------

  const totalUsers = users.length;
  const activeUsers = users.filter((u) => u.is_active).length;
  const totalProjects = projects.length;
  const totalOrgs = organizations.length;

  const countProjectsByAssessment = (name: string) =>
    projects.filter((p) => p.scheduler_assessment === name).length;

  const evaluatingProjects = countProjectsByAssessment("evaluating");
  const waitingProjects = countProjectsByAssessment("waiting");
  const solvingProjects = countProjectsByAssessment("solving");
  const doneProjects = countProjectsByAssessment("done");
  const impossibleProjects = countProjectsByAssessment("impossible!");
  const invalidProjects = countProjectsByAssessment("invalid!");
  const aboLimitProjects = countProjectsByAssessment("size-limit, please upgrade");

  const otherProjects = projects.filter(
    (p) =>
      ![
        "evaluating",
        "waiting",
        "solving",
        "done",
        "impossible!",
        "invalid!",
        "size-limit, please upgrade",
      ].includes(p.scheduler_assessment ?? "")
  ).length;

  const pct = (part: number, total: number): string =>
    total > 0 ? `${Math.round((100 * part) / total)}%` : "-";

  const projectsBreakdownText =
    totalProjects > 0
      ? `done ${pct(doneProjects, totalProjects)} · solving ${pct(solvingProjects, totalProjects)} · waiting ${pct(waitingProjects, totalProjects)} · other ${pct(otherProjects, totalProjects)}`
      : "-";

  const countOrgsByAbo = (name: string) =>
    organizations.filter((o) => o.abo === name).length;

  const freeOrgs = countOrgsByAbo("free");
  const basicOrgs = countOrgsByAbo("basic");
  const proOrgs = countOrgsByAbo("pro");
  const otherOrgs = organizations.filter(
    (o) => !["free", "basic", "pro"].includes(o.abo ?? "")
  ).length;

  const orgsBreakdownText =
    totalOrgs > 0
      ? `free ${pct(freeOrgs, totalOrgs)} · basic ${pct(basicOrgs, totalOrgs)} · pro ${pct(proOrgs, totalOrgs)} · other ${pct(otherOrgs, totalOrgs)}`
      : "-";

  const ProjectsInSolve = projects.filter((p) => p.solve_lock_token).length;

  const staleLocks = projects.filter((p) => {
    if (!p.solve_locked_at) return false;
    const ageMs = Date.now() - new Date(p.solve_locked_at).getTime();
    return ageMs > 60 * 60 * 1000;
  }).length;

  const avgUrgency =
    projects.length > 0
      ? Math.round(
          projects.reduce((sum, p) => sum + (p.solve_urgency ?? 0), 0) /
            projects.length
        )
      : 0;

  const recentlyActiveProjects = projects.filter((p) => {
    if (!p.ui_activity_at) return false;
    const ageMs = Date.now() - new Date(p.ui_activity_at).getTime();
    return ageMs < 60_000;
  }).length;

  const totalSolveTime = organizations.reduce(
    (sum, o) => sum + (o.all_projects_solve_duration ?? 0),
    0
  );

  const topOrgBySolveTime =
    organizations.length === 0
      ? null
      : organizations
          .slice()
          .sort(
            (a, b) =>
              (b.all_projects_solve_duration ?? 0) -
              (a.all_projects_solve_duration ?? 0)
          )[0];

  const qualityValues = projects
    .map((p) => p.quality)
    .filter((q): q is number => typeof q === "number");

  const avgQuality =
    qualityValues.length > 0
      ? formatPercent(
          qualityValues.reduce((a, b) => a + b, 0) / qualityValues.length
        )
      : "-";

  

  // ---------------- Fokus-Projekt (Auto vs. manuell) ----------------

  const focusedProject: ProjectDebug | null =
    projects.length === 0
      ? null
      : selectedProjectId === null
        ? projects[0]
        : projects.find((p) => p.id === selectedProjectId) ?? projects[0];

  const rulesText = focusedProject?.rules_xml ?? "";
  const planText = focusedProject?.plan_csv ?? "";
  const atomsText = focusedProject?.atoms_json ?? "";
  const preparedRulesText = focusedProject?.prepared_rules_xml ?? "";
  const statsText = focusedProject?.stats_json ?? "";
  const fulfillmentsText = focusedProject?.fulfillments_json ?? "";
  const diffText = focusedProject?.diff_json ?? "";
  const periodStatesText = focusedProject?.period_states_json ?? "";
  const syntaxchecksText = focusedProject?.syntaxchecks_json ?? "";

  // Header-Zeile
  let focusedHeaderText = "no project";
  if (focusedProject) {
    const q = formatPercent(focusedProject.quality);
    const i = formatPercent(focusedProject.improvement, 4);
    const rules = focusedProject.rules_at
      ? formatDateTime(focusedProject.rules_at)
      : "-";
    const solve = focusedProject.solve_at
      ? formatDateTime(focusedProject.solve_at)
      : "-";
    const fulfill = focusedProject?.last_fulfillments_at
      ? formatDateTime(focusedProject.last_fulfillments_at)
      : "-";
    const assessment = focusedProject.scheduler_assessment ?? "-";
    const urgency = focusedProject.solve_urgency ?? 0;
    const solverStatus = focusedProject.solver_status ?? "-";
    const lockOwner = focusedProject.solve_lock_owner ?? "-";
    focusedHeaderText =
      `${focusedProject.id} · ${assessment} · ${solverStatus} · lock ${lockOwner} · Q ${q} · I ${i} · R ${rules} · S ${solve} · F ${fulfill} · ${focusedProject.name}`;
  }

  // ---------------- CSV-/Text-Export-Handler ----------------

  const handleExportProjectsCsv = () => {
    exportCsv(
      "projects.csv",
      [
        "id",
        "organization_id",
        "name",
        "has_fresh_plan",
        "quality",
        "improvement",
        "rules_at",
        "solve_at",
        "created_at",
      ],
      projects.map((p) => [
        p.id,
        p.organization_id,
        p.name,
        p.has_fresh_plan ? 1 : 0,
        p.quality,
        p.improvement,
        p.rules_at ? formatDateTime(p.rules_at) : "",
        p.solve_at ? formatDateTime(p.solve_at) : "",
        p.created_at ? formatDateTime(p.created_at) : "",
      ])
    );
  };

  const handleExportUsersCsv = () => {
    exportCsv(
      "users.csv",
      [
        "id",
        "organization_id",
        "name",
        "role",
        "is_active",
        "active_project_id",
        "created_at",
        "login_at",
      ],
      users.map((u) => [
        u.id,
        u.organization_id,
        u.name,
        u.role,
        u.is_active ? 1 : 0,
        u.active_project_id ?? "",
        u.created_at ? formatDateTime(u.created_at) : "",
        u.login_at ? formatDateTime(u.login_at) : "",
      ])
    );
  };

  const handleExportOrgsCsv = () => {
    exportCsv(
      "organizations.csv",
      ["id", "name", "abo", "created_at"],
      organizations.map((o) => [
        o.id,
        o.name,
        o.abo,
        o.created_at ? formatDateTime(o.created_at) : "",
      ])
    );
  };

  const handleExportRules = () => {
    if (!focusedProject) return;
    exportText("rules.xml", rulesText);
  };

  const handleExportPreparedRules = () => {
    if (!focusedProject) return;
    exportText("prepared_rules.xml", preparedRulesText);
  };

  const handleExportPlan = () => {
    if (!focusedProject) return;
    exportText("plan.csv", planText);
  };

  const handleExportAtoms = () => {
    if (!focusedProject) return;
    exportText("atoms.json", atomsText);
  };

  const handleExportStats = () => {
    if (!focusedProject) return;
    exportText("stats.json", statsText);
  };

  const handleExportFulfillments = () => {
    if (!focusedProject) return;
    exportText("fulfillments.json", fulfillmentsText);
  };

  const handleExportDiff = () => {
    if (!focusedProject) return;
    exportText("diff.json", diffText);
  };

  const handleExportPeriodStates = () => {
    if (!focusedProject) return;
    exportText("period_states.json", periodStatesText);
  };

  const handleExportSyntaxchecks = () => {
    if (!focusedProject) return;
    exportText("syntaxchecks.json", syntaxchecksText);
  };

  const handleTogglePause = () => {
    setIsPaused((prev) => !prev);
  };

  const handleResetFocus = () => {
    setSelectedProjectId(null);
  };

  const handleExecuteSql = async () => {
    const sql = sqlInput.trim();
    if (!sql) return;

    setSqlBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/debug/sql`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sql }),
      });

      if (!res.ok) {
        const text = await res.text();
        setSqlResult({
          message: `HTTP ${res.status}: ${text}`,
          columns: null,
          rows: null,
          rowcount: null,
        });
        return;
      }

      const data = (await res.json()) as SqlResult;
      setSqlResult(data);
    } catch (e: any) {
      setSqlResult({
        message: `ERROR: ${e?.message ?? e}`,
        columns: null,
        rows: null,
        rowcount: null,
      });
    } finally {
      setSqlBusy(false);
    }
  };

  const handleExportSqlResultCsv = () => {
    if (!sqlResult || !sqlResult.columns || !sqlResult.rows) return;
    exportCsv("sql_result.csv", sqlResult.columns, sqlResult.rows);
  };

  return (
    <div style={styles.appRoot}>
      {/* Titel + Refresh-Toggle */}
      <div style={styles.headerRow}>
        <h1 style={styles.title}>zeitgeist monitor</h1>
        <button
          style={{
            ...styles.toggleButton,
            ...(isPaused ? styles.toggleButtonPaused : {}),
          }}
          onClick={handleTogglePause}
        >
          {isPaused ? "⏸ halted" : "⏵ running"}
        </button>
      </div>

      {error && (
        <div style={{
          ...styles.errorBox,
          ...(dbDown ? {
            background: "linear-gradient(135deg,#ff416c22,#ff4b2b11)",
            border: "1.5px solid rgba(255,65,108,0.5)",
            borderRadius: 10,
            display: "flex",
            alignItems: "center",
            gap: "10px",
            fontSize: "0.95rem",
            fontWeight: 600,
          } : {})
        }}>
          {dbDown && <span style={{ fontSize: "1.3rem" }}>🗄️</span>}
          {error}
        </div>
      )}

      {/* KPIs */}
      <div style={styles.kpiRow}>
        <div style={styles.kpiBox}>
          <div style={styles.kpiLabel}>
            {kpiLabel("Total Users", "Total number of users in the system.")}
          </div>
          <div style={styles.kpiValue}>{totalUsers}</div>
        </div>

        <div style={styles.kpiBox}>
          <div style={styles.kpiLabel}>
            {kpiLabel(
              "Active Users",
              "Users with is_active = true (currently enabled accounts)."
            )}
          </div>
          <div style={styles.kpiValue}>{activeUsers}</div>
        </div>

        <div style={styles.kpiBox}>
          <div style={styles.kpiLabel}>
            {kpiLabel(
              "Projects",
              "Total number of projects. Distribution below shows share by scheduler_assessment."
            )}
          </div>
          <div style={styles.kpiValue}>{totalProjects}</div>
          <div style={styles.kpiSubvalue}>{projectsBreakdownText}</div>
        </div>

        <div style={styles.kpiBox}>
          <div style={styles.kpiLabel}>
            {kpiLabel(
              "Organizations",
              "Total number of organizations. Distribution below shows share by subscription."
            )}
          </div>
          <div style={styles.kpiValue}>{totalOrgs}</div>
          <div style={styles.kpiSubvalue}>{orgsBreakdownText}</div>
        </div>

        <div style={styles.kpiBox}>
          <div style={styles.kpiLabel}>
            {kpiLabel(
              "Avg Urgency",
              "Average solve_urgency across all projects. Higher values indicate more pending or delayed work."
            )}
          </div>
          <div style={styles.kpiValue}>{avgUrgency}</div>
        </div>

        <div style={styles.kpiBox}>
          <div style={styles.kpiLabel}>
            {kpiLabel(
              "Active Projects",
              "Projects with recent user interaction (ui_activity_at within last 60 seconds). Reflects live user activity."
            )}
          </div>
          <div style={styles.kpiValue}>{recentlyActiveProjects}</div>
        </div>

        <div style={styles.kpiBox}>
          <div style={styles.kpiLabel}>
            {kpiLabel(
              "Total Solve Time",
              "Cumulative solver runtime across all organizations (all_projects_solve_duration)."
            )}
          </div>
          <div style={styles.kpiValue}>{formatSeconds(totalSolveTime)}</div>
        </div>

        <div style={styles.kpiBox}>
          <div style={styles.kpiLabel}>
            {kpiLabel(
              "Avg Quality",
              "Average solution quality across all projects (projects.quality)."
            )}
          </div>
          <div style={styles.kpiValue}>{avgQuality}</div>
        </div>

        <div style={styles.kpiBox}>
          <div style={styles.kpiLabel}>
            {kpiLabel(
              "Stale Locks",
              "Projects with a solve lock older than 60 minutes (solve_locked_at). Indicates potentially stuck solver jobs."
            )}
          </div>
          <div style={styles.kpiValue}>{staleLocks}</div>
        </div>

        <div style={styles.kpiBox}>
          <div style={styles.kpiLabel}>
            {kpiLabel(
              "Projects in Solver",
              "Projects currently locked by a worker (solve_lock_token IS NOT NULL)"
            )}
          </div>
          <div style={styles.kpiValue}>{ProjectsInSolve}</div>
        </div>

      </div>

      {/* Fokus-Projekt + rules/plan/stats */}
      <div style={styles.currentProjectSection}>
        <div style={styles.currentProjectHeader}>
          <div style={styles.currentProjectTitle}>{focusedHeaderText}</div>
          <button
            style={{
              ...styles.focusModeButton,
              ...(selectedProjectId !== null
                ? styles.focusModeButtonActive
                : {}),
            }}
            onClick={() => {
              if (selectedProjectId !== null) {
                setSelectedProjectId(null); // zurück in Auto-Modus
              }
            }}
          >
            {selectedProjectId !== null ? "focus" : "auto"}
          </button>
        </div>

        <div style={styles.payloadGrid}>
          <div style={styles.payloadBox}>
            <div style={styles.payloadHeader}>
              <div style={styles.payloadLabel}>rules.xml</div>
              <button
                style={styles.exportButtonSmall}
                onClick={handleExportRules}
                disabled={!focusedProject}
              >
                export
              </button>
            </div>
            <textarea
              readOnly
              value={rulesText}
              style={styles.payloadTextarea}
              placeholder="(no data)"
            />
          </div>

          <div style={styles.payloadBox}>
            <div style={styles.payloadHeader}>
              <div style={styles.payloadLabel}>prepared_rules.xml</div>
              <button
                style={styles.exportButtonSmall}
                onClick={handleExportPreparedRules}
                disabled={!focusedProject}
              >
                export
              </button>
            </div>
            <textarea
              readOnly
              value={preparedRulesText}
              style={styles.payloadTextarea}
              placeholder="(no data)"
            />
          </div>

          <div style={styles.payloadBox}>
            <div style={styles.payloadHeader}>
              <div style={styles.payloadLabel}>plan.csv</div>
              <button
                style={styles.exportButtonSmall}
                onClick={handleExportPlan}
                disabled={!focusedProject}
              >
                export
              </button>
            </div>
            <textarea
              readOnly
              value={planText}
              style={styles.payloadTextarea}
              placeholder="(no data)"
            />
          </div>

          <div style={styles.payloadBox}>
            <div style={styles.payloadHeader}>
              <div style={styles.payloadLabel}>atoms.json</div>
              <button
                style={styles.exportButtonSmall}
                onClick={handleExportAtoms}
                disabled={!focusedProject}
              >
                export
              </button>
            </div>
            <textarea
              readOnly
              value={atomsText}
              style={styles.payloadTextarea}
              placeholder="(no data)"
            />
          </div>

          <div style={styles.payloadBox}>
            <div style={styles.payloadHeader}>
              <div style={styles.payloadLabel}>stats.json</div>
              <button
                style={styles.exportButtonSmall}
                onClick={handleExportStats}
                disabled={!focusedProject}
              >
                export
              </button>
            </div>
            <textarea
              readOnly
              value={statsText}
              style={styles.payloadTextarea}
              placeholder="(no data)"
            />
          </div>

          <div style={styles.payloadBox}>
            <div style={styles.payloadHeader}>
              <div style={styles.payloadLabel}>fulfillments.json</div>
              <button
                style={styles.exportButtonSmall}
                onClick={handleExportFulfillments}
                disabled={!focusedProject}
              >
                export
              </button>
            </div>
            <textarea
              readOnly
              value={fulfillmentsText}
              style={styles.payloadTextarea}
              placeholder="(no data)"
            />
          </div>

          <div style={styles.payloadBox}>
            <div style={styles.payloadHeader}>
              <div style={styles.payloadLabel}>diff.json</div>
              <button
                style={styles.exportButtonSmall}
                onClick={handleExportDiff}
                disabled={!focusedProject}
              >
                export
              </button>
            </div>
            <textarea
              readOnly
              value={diffText}
              style={styles.payloadTextarea}
              placeholder="(no data)"
            />
          </div>

          <div style={styles.payloadBox}>
            <div style={styles.payloadHeader}>
              <div style={styles.payloadLabel}>period_states.json</div>
              <button
                style={styles.exportButtonSmall}
                onClick={handleExportPeriodStates}
                disabled={!focusedProject}
              >
                export
              </button>
            </div>
            <textarea
              readOnly
              value={periodStatesText}
              style={styles.payloadTextarea}
              placeholder="(no data)"
            />
          </div>

          <div style={styles.payloadBox}>
            <div style={styles.payloadHeader}>
              <div style={styles.payloadLabel}>syntaxchecks.json</div>
              <button
                style={styles.exportButtonSmall}
                onClick={handleExportSyntaxchecks}
                disabled={!focusedProject}
              >
                export
              </button>
            </div>
            <textarea
              readOnly
              value={syntaxchecksText}
              style={styles.payloadTextarea}
              placeholder="(no data)"
            />
          </div>

        </div>
      </div>

      {/* Tabellen-Bereich: Projekte, User, Organisationen */}
      <div style={styles.sectionsContainer}>
        {/* Projects Section */}
        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={{ margin: 0 }}>projects</h2>
            <div style={styles.sectionHeaderRight}>
              <button
                style={styles.exportButton}
                onClick={handleExportProjectsCsv}
                disabled={projects.length === 0}
              >
                export
              </button>
              <button
                style={styles.collapseButton}
                onClick={() => setShowProjects((v) => !v)}
              >
                {showProjects ? "▾" : "▸"}
              </button>
            </div>
          </div>
          {showProjects && (
            <div style={styles.scrollArea}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.tableHeaderCell}>ID</th>
                    <th style={styles.tableHeaderCell}>Org</th>
                    <th style={{ ...styles.tableHeaderCell, ...styles.nameCol }}>Name</th>
                    <th style={styles.tableHeaderCell}>assessment</th>
                    <th style={styles.tableHeaderCell}>solver_mode</th>
                    <th style={styles.tableHeaderCell}>urgency</th>
                    <th style={styles.tableHeaderCell}>in_solver</th>
                    <th style={styles.tableHeaderCell}>lock_owner</th>
                    <th style={styles.tableHeaderCell}>locked_at</th>
                    <th style={styles.tableHeaderCell}>estimate_at</th>
                    <th style={styles.tableHeaderCell}>solver_status</th>
                    <th style={styles.tableHeaderCell}>rules_at</th>
                    <th style={styles.tableHeaderCell}>solve_at</th>
                    <th style={styles.tableHeaderCell}>ui_activity_at</th>
                    <th style={styles.tableHeaderCell}>has_fresh</th>
                    <th style={styles.tableHeaderCell}>quality</th>
                    <th style={styles.tableHeaderCell}>improvement</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((p) => {
                    const isFocused =
                      focusedProject !== null && p.id === focusedProject.id;
                    return (
                      <tr
                        key={p.id}
                        onClick={() => setSelectedProjectId(p.id)}
                        style={{
                          cursor: "pointer",
                          ...(isFocused ? styles.projectRowSelected : {}),
                        }}
                      >
                        <td>{p.id}</td>
                        <td>{p.organization_id}</td>
                        <td style={styles.nameCell} title={p.name}>
                          {p.name}
                        </td>
                        <td>{p.scheduler_assessment ?? "-"}</td>
                        <td>{p.solver_mode ?? "-"}</td>
                        <td>{p.solve_urgency ?? 0}</td>
                        <td>{p.solve_lock_token ? "🟢" : ""}</td>
                        <td>{p.solve_lock_owner ?? "-"}</td>
                        <td>{p.solve_locked_at ? formatDateTime(p.solve_locked_at) : "-"}</td>
                        <td>{p.solve_estimate_at ? formatDateTime(p.solve_estimate_at) : "-"}</td>
                        <td>{p.solver_status ?? "-"}</td>
                        <td>{p.rules_at ? formatDateTime(p.rules_at) : "-"}</td>
                        <td>{p.solve_at ? formatDateTime(p.solve_at) : "-"}</td>
                        <td>{p.ui_activity_at ? formatDateTime(p.ui_activity_at) : "-"}</td>
                        <td>{p.has_fresh_plan ? "🟢" : ""}</td>
                        <td>{formatPercent(p.quality)}</td>
                        <td>{formatPercent(p.improvement, 4)}</td>
                      </tr>
                    );
                  })}
                  {projects.length === 0 && (
                    <tr>
                      <td colSpan={17} style={{ textAlign: "center", opacity: 0.6 }}>
                        no projects
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Users Section */}
        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={{ margin: 0 }}>users</h2>
            <div style={styles.sectionHeaderRight}>
              <button
                style={styles.exportButton}
                onClick={handleExportUsersCsv}
                disabled={users.length === 0}
              >
                export
              </button>
              <button
                style={styles.collapseButton}
                onClick={() => setShowUsers((v) => !v)}
              >
                {showUsers ? "▾" : "▸"}
              </button>
            </div>
          </div>
          {showUsers && (
            <div style={styles.scrollArea}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.tableHeaderCell}>ID</th>
                    <th style={styles.tableHeaderCell}>Org</th>
                    <th style={{ ...styles.tableHeaderCell, ...styles.nameCol }}>
                      Name
                    </th>
                    <th style={styles.tableHeaderCell}>Role</th>
                    <th style={styles.tableHeaderCell}>active?</th>
                    <th style={styles.tableHeaderCell}>active_project</th>
                    <th style={styles.tableHeaderCell}>created_at</th>
                    <th style={styles.tableHeaderCell}>login_at</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id}>
                      <td>{u.id}</td>
                      <td>{u.organization_id}</td>
                      <td style={styles.nameCell} title={u.name}>
                        {u.name}
                      </td>
                      <td>{u.role}</td>
                      <td>{u.is_active ? "🟢" : ""}</td>
                      <td>{u.active_project_id ?? "-"}</td>
                      <td>{u.created_at ? formatDateTime(u.created_at) : "-"}</td>
                      <td>{u.login_at ? formatDateTime(u.login_at) : "-"}</td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr>
                      <td colSpan={8} style={{ textAlign: "center", opacity: 0.6 }}>
                        no users
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Organizations Section */}
        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={{ margin: 0 }}>organizations</h2>
            <div style={styles.sectionHeaderRight}>
              <button
                style={styles.exportButton}
                onClick={handleExportOrgsCsv}
                disabled={organizations.length === 0}
              >
                export
              </button>
              <button
                style={styles.collapseButton}
                onClick={() => setShowOrgs((v) => !v)}
              >
                {showOrgs ? "▾" : "▸"}
              </button>
            </div>
          </div>
          {showOrgs && (
            <div style={styles.scrollArea}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.tableHeaderCell}>ID</th>
                    <th style={{ ...styles.tableHeaderCell, ...styles.nameCol }}>
                      Name
                    </th>
                    <th style={styles.tableHeaderCell}>Abo</th>
                    <th style={styles.tableHeaderCell}>total_solve_time</th>
                    <th style={styles.tableHeaderCell}>created_at</th>
                  </tr>
                </thead>
                <tbody>
                  {organizations.map((o) => (
                    <tr key={o.id}>
                      <td>{o.id}</td>
                      <td style={styles.nameCell} title={o.name}>
                        {o.name}
                      </td>
                      <td>{o.abo}</td>
                      <td>{formatSeconds(o.all_projects_solve_duration)}</td>
                      <td>{o.created_at ? formatDateTime(o.created_at) : "-"}</td>
                    </tr>
                  ))}
                  {organizations.length === 0 && (
                    <tr>
                      <td colSpan={4} style={{ textAlign: "center", opacity: 0.6 }}>
                        no organizations
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* SQL-Konsole */}
        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={{ margin: 0 }}>SQL console</h2>
          </div>
          <div style={styles.sqlLayout}>
            <div style={styles.sqlInputBox}>
              <div style={styles.sqlLabel}>SQL</div>
              <textarea
                style={styles.sqlTextarea}
                value={sqlInput}
                onChange={(e) => setSqlInput(e.target.value)}
                placeholder="z.B. SELECT * FROM users LIMIT 10;"
              />
              <div style={styles.sqlButtonsRow}>
                <button
                  style={styles.executeButton}
                  onClick={handleExecuteSql}
                  disabled={sqlBusy || !sqlInput.trim()}
                >
                  {sqlBusy ? "running..." : "execute"}
                </button>
                <button
                  style={styles.exportButton}
                  onClick={handleExportSqlResultCsv}
                  disabled={
                    !sqlResult || !sqlResult.columns || !sqlResult.rows || sqlResult.rows.length === 0
                  }
                >
                  export result
                </button>
              </div>
            </div>
            <div style={styles.sqlResultBox}>
              <div style={styles.sqlLabel}>Result</div>
              <div style={styles.sqlMessage}>
                {sqlResult ? sqlResult.message : "(no result yet)"}
                {sqlResult && sqlResult.rowcount !== null && (
                  <span style={{ marginLeft: "0.5rem", opacity: 0.7 }}>
                    ({sqlResult.rowcount} rows)
                  </span>
                )}
              </div>
              <div style={styles.sqlResultTableContainer}>
                {sqlResult && sqlResult.columns && sqlResult.rows && sqlResult.rows.length > 0 && (
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        {sqlResult.columns.map((c) => (
                          <th key={c} style={styles.tableHeaderCell}>
                            {c}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sqlResult.rows.map((row, idx) => (
                        <tr key={idx}>
                          {row.map((cell, j) => (
                            <td key={j}>{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </section>

      </div>

    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  appRoot: {
    fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
    padding: "1.5rem",
    backgroundColor: "#ffffff",
    color: "#000000",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "1rem",
    marginBottom: "0.5rem",
  },
  title: {
    fontSize: "2rem",
    marginBottom: "0.25rem",
  },
  errorBox: {
    backgroundColor: "#fee",
    border: "1px solid #f99",
    padding: "0.5rem",
    marginBottom: "1rem",
    color: "#900",
  },
  kpiRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "0.75rem",
    marginBottom: "1rem",
  },
  kpiBox: {
    border: "1px solid #ddd",
    borderRadius: "6px",
    padding: "0.5rem 0.75rem",
    backgroundColor: "#fafafa",
  },
  kpiLabel: {
    fontSize: "0.75rem",
    opacity: 0.7,
    marginBottom: "0.2rem",
  },
  kpiValue: {
    fontSize: "1rem",
    fontWeight: 600,
  },
  kpiSubvalue: {
    fontSize: "0.72rem",
    opacity: 0.75,
    marginTop: "0.2rem",
    lineHeight: 1.3,
  },

  currentProjectSection: {
    marginBottom: "1.5rem",
    border: "1px solid #ddd",
    borderRadius: "6px",
    padding: "0.75rem",
    backgroundColor: "#fafafa",
  },
  currentProjectHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "1rem",
    marginBottom: "0.5rem",
    flexWrap: "wrap",
  },
  currentProjectTitle: {
    fontSize: "0.9rem",
    fontWeight: 600,
  },
  payloadGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: "0.5rem",
  },
  payloadBox: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  payloadHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "0.2rem",
    gap: "0.5rem",
  },
  payloadLabel: {
    fontSize: "0.75rem",
    opacity: 0.7,
  },
  payloadTextarea: {
    fontFamily:
      "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
    fontSize: "0.75rem",
    borderRadius: "4px",
    border: "1px solid #ccc",
    padding: "0.25rem 0.35rem",
    height: "8rem",
    resize: "none",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "1rem",
    alignItems: "stretch",
  },
  column: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  columnHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "0.25rem",
  },
  scrollArea: {
    border: "1px solid #ddd",
    borderRadius: "6px",
    maxHeight: "60vh",
    overflowY: "auto",
    padding: "0.25rem",
    backgroundColor: "white",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "0.8rem",
  },
  tableHeaderCell: {
    textAlign: "left",
  },
  nameCol: {
    maxWidth: "160px",
  },
  nameCell: {
    maxWidth: "160px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  projectRowSelected: {
    backgroundColor: "#0a0",
    color: "white",
  },
  exportButton: {
    fontSize: "0.8rem",
    padding: "0.25rem 0.6rem",
    borderRadius: "4px",
    border: "1px solid #333",
    backgroundColor: "#333",
    color: "white",
    cursor: "pointer",
  },
  exportButtonSmall: {
    fontSize: "0.7rem",
    padding: "0.15rem 0.4rem",
    borderRadius: "4px",
    border: "1px solid #333",
    backgroundColor: "#333",
    color: "white",
    cursor: "pointer",
  },
  toggleButton: {
    fontSize: "0.8rem",
    padding: "0.3rem 0.7rem",
    borderRadius: "4px",
    border: "1px solid #0a0",
    backgroundColor: "#0a0",
    color: "white",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  toggleButtonPaused: {
    border: "1px solid #888",
    backgroundColor: "#888",
  },
  focusModeButton: {
    fontSize: "0.8rem",
    padding: "0.25rem 0.6rem",
    borderRadius: "4px",
    border: "1px solid #0a0",
    backgroundColor: "#0a0",
    color: "white",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  focusModeButtonActive: {
    border: "1px solid #888",
    backgroundColor: "#888",
  },

  sectionsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
  },

  section: {
    border: "1px solid #ddd",
    borderRadius: "6px",
    padding: "0.75rem",
    backgroundColor: "#fafafa",
  },

  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "0.25rem",
  },

  sectionHeaderRight: {
    display: "flex",
    gap: "0.5rem",
    alignItems: "center",
  },

  collapseButton: {
    fontSize: "0.8rem",
    padding: "0.1rem 0.4rem",
    borderRadius: "4px",
    border: "1px solid #333",
    backgroundColor: "#fff",
    cursor: "pointer",
  },

  sqlLayout: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.2fr) minmax(0, 1.8fr)",
    gap: "0.75rem",
    marginTop: "0.5rem",
  },

  sqlInputBox: {
    display: "flex",
    flexDirection: "column",
    gap: "0.25rem",
  },

  sqlResultBox: {
    display: "flex",
    flexDirection: "column",
    gap: "0.25rem",
  },

  sqlLabel: {
    fontSize: "0.75rem",
    opacity: 0.7,
  },

  sqlTextarea: {
    fontFamily:
      "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
    fontSize: "0.8rem",
    minHeight: "6rem",
    borderRadius: "4px",
    border: "1px solid #ccc",
    padding: "0.4rem",
    resize: "vertical",
  },

  sqlButtonsRow: {
    display: "flex",
    gap: "0.5rem",
    marginTop: "0.25rem",
  },

  executeButton: {
    fontSize: "0.8rem",
    padding: "0.25rem 0.6rem",
    borderRadius: "4px",
    border: "2px solid #f0f",
    backgroundColor: "#707",
    color: "white",
    cursor: "pointer",
  },

  sqlMessage: {
    fontSize: "0.8rem",
    marginBottom: "0.25rem",
  },

  sqlResultTableContainer: {
    border: "1px solid #ddd",
    borderRadius: "4px",
    maxHeight: "40vh",
    overflow: "auto",
    backgroundColor: "#fff",
  },
};

export default MonitorApp;